import { useState, type FormEvent } from "react";

import { Button, Card, CardBody, CardHead } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useSetOrganizationAddress } from "@/hooks/useOrganizations";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { Organization, OrganizationAddress } from "@/types/organization";

const EMPTY: OrganizationAddress = {
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  zip_code: "",
};

/** Endereço 1:1 da organização (texto plano). Editável com organizations.write. */
export function OrgAddressSection({ workspaceId, org }: { workspaceId: number; org: Organization }) {
  const save = useSetOrganizationAddress(workspaceId, org.id);
  const [addr, setAddr] = useState<OrganizationAddress>({ ...EMPTY, ...(org.address ?? {}) });
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync(addr);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  const field = (label: string, value: string | null, onChange: (v: string) => void, ph = "") => (
    <label className="form-field">
      <span>{label}</span>
      <input
        className="form-input"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={ph}
      />
    </label>
  );

  return (
    <Card>
      <CardHead title="Endereço" />
      <CardBody>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {error && <div className="form-error">{error}</div>}
          <div className="onb-field-grid onb-grid-2">
            {field("Logradouro", addr.street, (v) => setAddr((a) => ({ ...a, street: v })))}
            {field("Número", addr.number, (v) => setAddr((a) => ({ ...a, number: v })))}
            {field("Complemento", addr.complement, (v) => setAddr((a) => ({ ...a, complement: v })))}
            {field("Bairro", addr.neighborhood, (v) => setAddr((a) => ({ ...a, neighborhood: v })))}
            {field("Cidade", addr.city, (v) => setAddr((a) => ({ ...a, city: v })))}
            {field("UF", addr.state, (v) => setAddr((a) => ({ ...a, state: v })), "SP")}
            {field("CEP", addr.zip_code, (v) => setAddr((a) => ({ ...a, zip_code: v })), "00000-000")}
          </div>
          <Guard permission="organizations.write">
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="primary" type="submit" disabled={save.isPending}>
                {save.isPending ? "Salvando…" : "Salvar endereço"}
              </Button>
            </div>
          </Guard>
        </form>
      </CardBody>
    </Card>
  );
}
